import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpen,
  ClipboardList,
  GraduationCap,
  Layers,
  Library,
  LineChart,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  Target,
} from "lucide-react";
import { useWorkspace } from "@/stores/workspace";
import { cn } from "@/lib/cn";

type Action = {
  id: string;
  label: string;
  hint?: string;
  keywords?: string[];
  icon: typeof Search;
  perform: () => void;
};

const OPEN_EVENT = "gslm:open-command-palette";

export function openCommandPalette() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function CommandPalette() {
  const setView = useWorkspace((s) => s.setView);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const actions: Action[] = useMemo(
    () => [
      { id: "learn", label: "Learn Hub", hint: "Home", icon: BookOpen, perform: () => setView("learn") },
      { id: "dashboard", label: "My Dashboard", hint: "Progress & history", icon: BookOpen, perform: () => setView("dashboard") },
      { id: "tutor", label: "Talk to Maya", hint: "Avatar tutor", icon: Sparkles, perform: () => setView("tutor") },
      { id: "practice", label: "Adaptive Practice", hint: "Quant · Verbal · DI", icon: Target, perform: () => setView("practice") },
      { id: "exam", label: "Mock Exams", hint: "Timed simulations", icon: ClipboardList, perform: () => setView("exam") },
      { id: "topics", label: "Browse Topics", icon: GraduationCap, perform: () => setView("topics") },
      { id: "cards", label: "Flashcards", hint: "Spaced repetition", icon: Layers, perform: () => setView("flashcards") },
      { id: "score", label: "Score Predictor", icon: LineChart, perform: () => setView("score") },
      { id: "library", label: "Library & Resources", icon: Library, perform: () => setView("resources") },
      { id: "settings", label: "Settings", icon: SettingsIcon, perform: () => setView("settings") },
    ],
    [setView],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter(
      (a) =>
        a.label.toLowerCase().includes(q) ||
        a.hint?.toLowerCase().includes(q) ||
        a.keywords?.some((k) => k.toLowerCase().includes(q)),
    );
  }, [actions, query]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  const run = (a: Action) => {
    a.perform();
    setOpen(false);
    setQuery("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-start justify-center bg-foreground/25 pt-[15vh] backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl border border-border/70 bg-card shadow-editorial-lg"
          >
            <div className="flex items-center gap-3 border-b border-border/60 px-4">
              <Search className="size-4 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActive((i) => Math.min(i + 1, filtered.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActive((i) => Math.max(i - 1, 0));
                  } else if (e.key === "Enter" && filtered[active]) {
                    e.preventDefault();
                    run(filtered[active]);
                  }
                }}
                placeholder="Jump to a page or action…"
                className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              <kbd className="rounded border border-border/70 bg-background/70 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                Esc
              </kbd>
            </div>
            <ul className="max-h-80 overflow-auto p-1">
              {filtered.length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No matches for "{query}"
                </li>
              )}
              {filtered.map((a, i) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => run(a)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm",
                      i === active ? "bg-secondary" : "hover:bg-secondary/60",
                    )}
                  >
                    <a.icon className="size-4 text-muted-foreground" />
                    <span className="flex-1 font-medium">{a.label}</span>
                    {a.hint && (
                      <span className="text-xs text-muted-foreground">{a.hint}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t border-border/60 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <span>Think Big Techies</span>
              <span className="flex items-center gap-1.5">
                <kbd className="rounded border border-border/70 bg-background/70 px-1.5 py-0.5 font-mono">↑↓</kbd>
                <span>navigate</span>
                <kbd className="ml-2 rounded border border-border/70 bg-background/70 px-1.5 py-0.5 font-mono">⏎</kbd>
                <span>open</span>
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
